import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest } from 'rxjs';

import { CohortService } from '../../../core/api/cohort.service';
import { NotificationService } from '../../../core/api/notification.service';
import { UserService } from '../../../core/api/user.service';
import { SiteHelperService } from '../../../core/helper/site.service';
import { DialogSiteApprovedComponent } from '../../../shared/dialogs/dialog-site-approved/dialog-site-approved.component';
import { Site, User } from '../../../shared/models';
import { SpinnerService } from '../../../shared/spinner/spinner.service';
import * as fromApp from '../../../store';
import { selectAppConfigState } from '../../../store/app-config/app-config.selectors';
import { setActAsUser } from '../../../store/auth/auth.actions';
import { selectActAsUser, selectCurrentUser } from '../../../store/auth/auth.selectors';
import { selectNotificationState } from '../../../store/notification/notification.selectors';
import { clearActiveSite, deleteSite, getAvailableSites } from '../../../store/site/site.actions';
import { selectAvailableSites } from '../../../store/site/site.selectors';

@Component({
  selector: 'fc-dashboard-page',
  templateUrl: './dashboard-page.component.html',
  styleUrls: ['./dashboard-page.component.scss'],
})
export class DashboardPageComponent implements OnInit {
  actAsUser: User;
  availableSites: Site[];
  currentUser: User;
  helpEmail = '';
  helpPhone = '';
  landownerSelectionForm: UntypedFormGroup;
  landowners: User[];
  siteCount = 0;
  totalSitesAcres = 0;
  totalSitesValue = 0;

  destroyRef = inject(DestroyRef);

  constructor(
    readonly router: Router,
    public dialog: MatDialog,
    readonly cohortService: CohortService,
    readonly siteHelper: SiteHelperService,
    readonly notificationService: NotificationService,
    readonly formBuilder: UntypedFormBuilder,
    readonly route: ActivatedRoute,
    readonly store: Store<fromApp.AppState>,
    readonly userService: UserService,
    readonly spinnerService: SpinnerService
  ) {}

  ngOnInit() {
    this.store
      .select(selectAvailableSites)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(availableSites => {
        if (this.currentUser !== null) {
          this.availableSites = availableSites;

          this.totalSitesValue = 0;
          this.totalSitesAcres = 0;

          if (this.availableSites) {
            this.availableSites.forEach(site => {
              if (this.siteHelper.siteActiveStatusList.includes(site.status)) {
                const sitesValue = site.getAverageAnnualRevenue()[0];
                this.totalSitesValue += +sitesValue;
                this.totalSitesAcres += +site.area;
              }
            });
          }
          this.spinnerService.hide('sites-spinner');
        }
      });

    combineLatest([
      this.store.select(selectCurrentUser),
      this.store.select(selectActAsUser),
      this.store.select(selectNotificationState),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([currentUser, actAsUser, notificationState]) => {
        if (currentUser) {
          this.currentUser = currentUser;
          this.landowners = currentUser?.landowners;
          this.actAsUser = actAsUser;
          this.landownerSelectionForm = this.formBuilder.group({
            landowner: this.actAsUser ? this.actAsUser.cognitoSubject : '',
          });
          this.siteCount = notificationState.landOwner.paymentsAccountRequiresSetup;
          if (this.actAsUser && this.siteCount > 0 && this.actAsUser.id === notificationState.landOwner.userId) {
            // Display Dialog to set up payment
            const hasStripeAccounts =
              this.actAsUser.stripeAccounts?.filter(sa => sa.stripeUserIdentifier !== null).length > 0;
            this.openSiteApprovedDialog(hasStripeAccounts);
          }

          this.store.dispatch(getAvailableSites());
          this.store.dispatch(clearActiveSite());
          this.spinnerService.show('sites-spinner');
        }
      });

    this.store
      .select(selectAppConfigState)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(appConfig => {
        this.helpPhone = appConfig.config['fc_help_phone'];
        this.helpEmail = appConfig.config['fc_help_email'];
      });
  }

  getActAsUserSubject(): string {
    return this.actAsUser ? this.actAsUser.cognitoSubject : '';
  }

  changeLandowner(event: any): void {
    if (event.value == null) {
      this.store.dispatch(setActAsUser({ data: null }));
    } else {
      const actAsUser = new User(this.landowners.find(l => l.cognitoSubject === event.value));
      if (!this.actAsUser || this.actAsUser.id !== actAsUser.id) {
        this.availableSites = null;
        this.store.dispatch(setActAsUser({ data: actAsUser }));
        this.notificationService.initNotificationChecks();
      }
    }
  }

  doDeleteSite(site: Site): void {
    if (this.siteHelper.sitePendingStatusList.includes(site.status)) {
      this.store.dispatch(deleteSite({ id: site.id }));
    } else {
      this.siteHelper.showTerminateDialog();
    }
  }

  doGoToNewSite(): void {
    this.router.navigateByUrl('/site/new-site');
  }

  openSiteApprovedDialog(hasStripeAccounts: boolean): void {
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(DialogSiteApprovedComponent, {
        panelClass: 'fc-site-approved-dialog',
        data: {
          hasStripeAccounts,
          isSelf: this.currentUser.id == this.actAsUser.id,
        },
        autoFocus: false,
      });

      dialogRef.afterClosed().subscribe(result => {
        if (this.currentUser.id == this.actAsUser.id && result === true) {
          this.router.navigateByUrl('/account/profile');
        }
      });
    }
  }
}
